import {Link, TargetBlank} from '@wandb/weave/common/util/links';
import {assertUnreachable} from '@wandb/weave/common/util/types';
// @todo: Migrate to @wandb/components. For more details see:
// https://wandb.github.io/components/?path=/docs/migration-guide--docs
// eslint-disable-next-line wandb/no-deprecated-imports
import {Button} from '@wandb/weave/components/Button';
// @todo: Migrate to 'Icon' from @wandb/components. For more details see:
// https://wandb.github.io/components/?path=/docs/migration-guide--docs
// eslint-disable-next-line wandb/no-deprecated-imports
import {Icon, IconInfo} from '@wandb/weave/components/Icon';
import {ExecutionResult} from 'graphql';
import React, {useContext, useEffect, useState} from 'react';
// @todo: Migrate to 'Toast' from @wandb/components. For more details see:
// https://wandb.github.io/components/?path=/docs/migration-guide--docs
// eslint-disable-next-line wandb/no-deprecated-imports
import {toast, ToastContent} from 'react-toastify';
import {useShallow} from 'zustand/react/shallow';

import {
  CreateFilterTriggerMutation,
  EventTriggeringConditionType,
  useUpdateFilterTriggerMutation,
} from '../../../../generated/graphql';
import {Analytics} from '../../../../services/analytics';
import {useIsAutomationEnabled} from '../../../../util/accounts/pricing';
import {propagateErrorsContext} from '../../../../util/errors';
import {toMongo} from '../../../../util/filters';
import {
  accountSettingsBillingTab,
  queue as queueUrl,
} from '../../../../util/urls';
import {Banner} from '../../../Banner/Banner';
import {BannerFlexWrapper} from '../../../Banner/Banner.styles';
import {AccountSelectorContext} from '../../../Search/SearchNav/AccountSelectorContextProvider';
import {
  createTriggerActionConfigForSlack,
  createTriggerActionConfigForWebhook,
  createTriggerEventConfigForRunMetric,
  createTriggerEventConfigForRunMetricChange,
  createTriggerEventConfigForRunMetricZscore,
  createTriggerEventConfigForRunState,
  TriggerActionConfig,
  TriggerEventConfig,
  useCreateAutomation,
  useCreateQueue,
} from '../hooks';
import {NotificationConfig} from '../notificationUtils';
import {getDefaultTriggerDescription} from '../utils';
import {
  ActionConfig,
  isValidActionConfig,
  isValidNotificationConfig,
  isValidWebhookConfig,
  useActionConfigStore,
  WebhookConfig,
} from './ActionStore';
import {
  AutomationStoreActions,
  Steps,
  useAutomationStore,
} from './AutomationStore';
import * as S from './CreateTriggerDrawerLoaded.styles';
import {DrawerPageActionConfiguration} from './DrawerPageActionConfiguration';
import {BasicPageFooter, DrawerPageBasic} from './DrawerPageBasic';
import {DrawerPageEventConfiguration} from './DrawerPageEventConfiguration';
import {DrawerPageSummary} from './DrawerPageSummary';
import {
  EventConfig,
  isEventValid,
  TriggerRootFilter,
  useEventStore,
} from './EventStore';
import {
  isValidRunMetricChangeConfig,
  RunMetricChangeConfig,
  useRunMetricChangeStore,
} from './RunMetricChangeSection';
import {
  isValidRunMetricThresholdConfig,
  RunMetricThresholdConfig,
  useRunMetricThresholdStore,
} from './RunMetricThresholdSection';
import {
  RunMetricZscoreConfig,
  useRunMetricZscoreStore,
  ZscoreMetricFilter,
} from './RunMetricZscoreSection';
import {
  isValidRunStateConfig,
  RunStateConfig,
  useRunStateStore,
} from './RunStateSection';

type CreateTriggerDrawerLoadedProps = {
  onClose: () => void;
  refetchProjectTriggers: () => Promise<any>;
  refetchCollectionTriggers?: () => Promise<any>;
};

export const CreateTriggerDrawerLoaded = ({
  onClose,
  refetchProjectTriggers,
  refetchCollectionTriggers,
}: CreateTriggerDrawerLoadedProps) => {
  const actionConfig = useActionConfigStore(useShallow(s => s));
  const jobConfig = useActionConfigStore(useShallow(s => s.job));
  const webhookConfig = useActionConfigStore(useShallow(s => s.webhook));
  const notificationConfig = useActionConfigStore(
    useShallow(s => s.notification)
  );
  const eventConfig = useEventStore(useShallow(s => s));

  // Event configuration state
  const step = useAutomationStore(useShallow(s => s.step));

  // Custom mutation hooks
  const {createQueue, loading: loadingCreateQueue} = useCreateQueue();
  const {
    createQueuedJobAutomation,
    createWebhookAutomation,
    loading: loadingCreateAutomation,
    createTriggerForEventAndAction,
  } = useCreateAutomation();
  const [updateTriggerMutation] = useUpdateFilterTriggerMutation({
    context: propagateErrorsContext(),
  });

  // Keeping this logging so that we can see the full config being created,
  // which is useful while debugging. the useEffect means it'll be low volume.
  useEffect(() => {
    if (step === Steps.SUMMARY) {
      console.log('eventConfig', eventConfig);
      console.log('actionConfig', actionConfig);
    }
  }, [eventConfig, actionConfig, step]);
  const triggerName = useAutomationStore(useShallow(s => s.triggerName));
  const triggerDescription = useAutomationStore(
    useShallow(s => s.triggerDescription)
  );

  const [createdAutomation, setCreatedAutomation] = useState<
    ExecutionResult<CreateFilterTriggerMutation> | undefined
  >();

  const createTrigger = async (
    triggerName: string
  ): Promise<{
    success: boolean;
    toastMessage: ToastContent;
  }> => {
    // This code accesses zustand store state directly - it's a bit of a hack,
    // but trying to move this createTriggers() function out is a lot of work due to
    // all of its dependencies. So for now I think it's cleaner to just do this.
    //
    // Why is this okay? Why don't we access getState/setState elsewhere?
    // For this code, we don't need to re-render the component when the state in the store changes,
    // we just need to grab the state at the point in time when the user clicks the button.

    const runMetricConfig = useRunMetricThresholdStore.getState();
    const runMetricChangeConfig = useRunMetricChangeStore.getState();
    const runStateConfig = useRunStateStore.getState();
    const runMetricZscoreConfig = useRunMetricZscoreStore.getState();

    // Pretty display for description
    const descriptionDict = getDefaultTriggerDescription(
      {
        eventType: eventConfig.eventType,
        triggerScopeType: eventConfig.scopeType,
        entityName: eventConfig.entityName,
        projectName: eventConfig.projectName,
        artifactCollectionName: eventConfig.scopeArtifactCollectionName,
        filters: JSON.stringify(toMongo(eventConfig.filters)),
        runMetricConfig,
        runMetricChangeConfig,
        runMetricZscoreConfig,
        notificationConfig,
        runStateConfig,
      },
      actionConfig.actionType,
      jobConfig.queuedJob?.name ?? '',
      jobConfig.queuedJobAlias ?? '',
      webhookConfig.webhook?.name,
      notificationConfig.teamSlackIntegration?.channelName
    );
    const defaultTriggerDescription = descriptionDict.stringVal;
    /*
    TECH DEBT NOTE: There are currently 2 different ways to create a trigger:
    * createTriggerForEventAndAction - used for run metric, run metric change, and run state (and slack notifications)
    * createQueuedJobAutomation - used for queue job triggered action
    * createWebhookAutomation - used for generic webhook triggered action

    createTriggerForEventAndAction is the "new" version that we should move towards.

    There's no reason to keep createWebhookAutomation/createQueuedJobAutomation. To remove them, we need to:
    1. test to make sure it works (note that webhook automation already works in the new path for some events, so it should be good)
    2. add an equivalent of createTriggerActionConfigForWebhook for QueueJobTriggeredAction

    */
    if (
      actionConfig.actionType === 'NotificationTriggeredAction' ||
      eventConfig.eventType === EventTriggeringConditionType.RunMetric ||
      eventConfig.eventType === EventTriggeringConditionType.RunMetricChange ||
      eventConfig.eventType === EventTriggeringConditionType.RunState ||
      eventConfig.eventType === EventTriggeringConditionType.RunMetricZscore ||
      eventConfig.initProps.isUpdating
    ) {
      const triggerEventConfigRes = getEventTriggerConfig(
        eventConfig,
        runMetricConfig,
        runMetricChangeConfig,
        runStateConfig,
        runMetricZscoreConfig
      );
      const triggerActionConfigRes = getActionTriggerConfig(
        actionConfig,
        notificationConfig,
        webhookConfig
      );
      if (!triggerEventConfigRes.success || !triggerActionConfigRes.success) {
        console.error('Could not create trigger config results:', {
          triggerEventConfigRes,
          triggerActionConfigRes,
        });
        return {
          success: false,
          toastMessage:
            'There was an error. Please refresh the page and try again.',
        };
      }

      if (eventConfig.initProps.isUpdating) {
        if (!eventConfig.initProps.existingTrigger?.id) {
          // This should be a theoretical only case
          return {
            success: false,
            toastMessage:
              'There was an error saving your updates. Please refresh the page and try again.',
          };
        }
        const updatedTrigger = await updateTriggerMutation({
          variables: {
            triggerID: eventConfig.initProps.existingTrigger?.id,
            name: triggerName,
            description: triggerDescription,
            ...triggerEventConfigRes.triggerEventConfig,
            ...triggerActionConfigRes.triggerActionConfig,
            enabled: true,
          },
        });
        setCreatedAutomation(updatedTrigger);
        if (updatedTrigger) {
          return {
            success: true,
            toastMessage: 'Automation updated successfully',
          };
        } else {
          return {
            success: false,
            toastMessage:
              'Something went wrong while trying to update this automation',
          };
        }
      } else {
        // the type assertions (`!` below) are safe because we just validated the config
        const createdTrigger = await createTriggerForEventAndAction(
          triggerEventConfigRes.triggerEventConfig,
          triggerActionConfigRes.triggerActionConfig,
          triggerName,
          triggerDescription,
          defaultTriggerDescription,
          true // at this time, all automations are enabled
        );

        setCreatedAutomation(createdTrigger);
        if (createdTrigger) {
          return {
            success: true,
            toastMessage: 'Automation created successfully',
          };
        } else {
          return {
            success: false,
            toastMessage:
              'Something went wrong while trying to create this automation',
          };
        }
      }
    } else if (actionConfig.actionType === 'QueueJobTriggeredAction') {
      if (!jobConfig.selectedQueue) {
        return {success: false, toastMessage: 'No queue selected'};
      }
      let queueID = jobConfig.selectedQueue.queueID;
      let queueName = jobConfig.selectedQueue.queue;
      if (jobConfig.selectedQueue.queue === '') {
        queueName = 'Starter queue';
        const createdQueue = await createQueue(
          actionConfig.entityName ?? '',
          jobConfig.targetProjectName ?? '',
          queueName
        );
        queueID = createdQueue ?? '';
      }
      const createdTrigger = await createQueuedJobAutomation(
        actionConfig.entityName ?? '',
        jobConfig.queuedJob,
        eventConfig.scopeType,
        eventConfig.scopeID,
        eventConfig.eventType,
        eventConfig.filters,
        jobConfig.queuedJobAlias,
        queueID,
        jobConfig.targetProjectName ?? '',
        jobConfig.overrides,
        triggerName,
        triggerDescription,
        defaultTriggerDescription
      );
      setCreatedAutomation(createdTrigger);
      const newItemId = createdTrigger?.data?.createFilterTrigger?.trigger?.id;
      if (newItemId) {
        return {
          success: true,
          toastMessage: (
            <S.SuccessfulQueue>
              <IconInfo width={48} height={48} />
              <S.SuccessfulQueueMessage>
                Your automation was successfully added to '{queueName}'.{' '}
                <TargetBlank
                  href={queueUrl(
                    actionConfig.entityName ?? '',
                    queueID,
                    'runs'
                  )}>
                  View queue
                </TargetBlank>
              </S.SuccessfulQueueMessage>
            </S.SuccessfulQueue>
          ),
        };
      }
    } else if (actionConfig.actionType === 'GenericWebhookTriggeredAction') {
      const createdTrigger = await createWebhookAutomation(
        eventConfig.scopeType,
        eventConfig.scopeID,
        eventConfig.eventType,
        eventConfig.filters,
        webhookConfig.webhook?.id ?? '',
        webhookConfig.webhookPayload,
        triggerName,
        triggerDescription,
        defaultTriggerDescription
      );
      setCreatedAutomation(createdTrigger);
      if (createdTrigger) {
        return {success: true, toastMessage: 'Automation created successfully'};
      }
    }

    return {
      success: false,
      toastMessage:
        'There was an error. Please refresh the page and try again.',
    };
  };

  // This is a wrapper around createTrigger that ensures we accomplish some common
  // code in the success and failure cases.
  const handleCreateTrigger = async (triggerName: string) => {
    let createSucceeded = false;
    try {
      const result = await createTrigger(triggerName);
      if (result.success) {
        onClose();
        toast.success(result.toastMessage);
        Analytics.track('Automation Creation', {
          type: 'completed',
          status: 'success',
          eventType: eventConfig.eventType,
          actionType: actionConfig.actionType,
          entryPoint: eventConfig.entryPoint,
        });
        createSucceeded = true;
      }
    } catch (error) {
      console.error('exception thrown during createTrigger', error);
    }
    if (createSucceeded) {
      try {
        await refetchProjectTriggers?.();
        await refetchCollectionTriggers?.();
      } catch (error) {
        toast.error(
          'There was an error refreshing the list of automations. Please refresh the page to see your new automation.'
        );
        console.error('exception thrown during refetch', error);
      }
      // regardless of whether the refetch worked, we will still consider it "done"
      // since the user doesn't need to do anything else, and we succeeded in creating the automation
      return;
    }

    // Don't close when there is an error - that's super annoying since the user
    // has to recreate the whole automation!
    toast.error(
      'There was an error creating your automation. Please try again.'
    );
    Analytics.track('Automation Creation', {
      type: 'completed',
      status: 'failed',
      eventType: eventConfig.eventType,
      actionType: actionConfig.actionType,
      entryPoint: eventConfig.entryPoint,
    });
  };
  const {selectedAccount} = useContext(AccountSelectorContext);
  const orgName = selectedAccount?.name ?? '';
  const hasAutomationAccess = useIsAutomationEnabled(selectedAccount);

  // if we're updating, the name *should* conflict, but it's easier
  // for code using this if we treat the automationNameConflict path
  // as an indicator of a problem, so we disable it if we're updating.
  const automationNameConflict =
    createdAutomation !== undefined || eventConfig.initProps.isUpdating
      ? false
      : (eventConfig.existingAutomations
          ?.filter(
            t =>
              t.scopeType === eventConfig.scopeType &&
              t.scopeID === eventConfig.scopeID
          )
          .some(t => t.name === triggerName) ?? false);

  return (
    <S.CreateTriggerDrawer>
      <S.CenteredFlex>
        <S.Header>
          {eventConfig.initProps.isUpdating
            ? 'Edit automation'
            : 'Create automation'}
        </S.Header>
        <S.CloseButton>
          <Button onClick={onClose} icon="close" size="large" variant="ghost" />
        </S.CloseButton>
      </S.CenteredFlex>
      <S.Divider></S.Divider>
      {!hasAutomationAccess ? (
        <div className="mt-20">
          <Banner variant="announcement">
            <BannerFlexWrapper>
              <Icon name="crown-pro" />
              <div>
                <span className="font-semibold">
                  Create notification automation.
                </span>{' '}
                Upgrade to access this feature
              </div>
              <Link
                className="font-semibold"
                to={accountSettingsBillingTab(orgName)}>
                Upgrade
              </Link>
            </BannerFlexWrapper>
          </Banner>
        </div>
      ) : (
        <>
          <S.CreateTriggerDrawerMain>
            {step === Steps.BASIC_METRIC_NOTIFICATION && <DrawerPageBasic />}
            {step === Steps.EVENT_CONFIGURATION && (
              <DrawerPageEventConfiguration />
            )}
            {step === Steps.ACTION_CONFIGURATION && (
              <DrawerPageActionConfiguration onClose={onClose} />
            )}
            {step === Steps.SUMMARY && (
              <DrawerPageSummary triggerNameExists={automationNameConflict} />
            )}
          </S.CreateTriggerDrawerMain>
          <S.Divider></S.Divider>
          <Footer
            step={step}
            setStep={AutomationStoreActions.setStep}
            loadingCreateQueue={loadingCreateQueue}
            loadingCreateAutomation={loadingCreateAutomation}
            createTrigger={handleCreateTrigger}
            triggerName={triggerName}
            automationNameConflict={automationNameConflict}
          />
        </>
      )}
    </S.CreateTriggerDrawer>
  );
};

// the next/back buttons need to be refreshed on every state change, so we
// put this in a separate component to avoid redrawing everything
const Footer = ({
  step,
  setStep,
  loadingCreateQueue,
  loadingCreateAutomation,
  createTrigger,
  triggerName,
  automationNameConflict,
}: {
  step: Steps;
  setStep: (step: Steps) => void;
  loadingCreateQueue: boolean;
  loadingCreateAutomation: boolean;
  createTrigger: (triggerName: string) => void;
  triggerName: string;
  automationNameConflict: boolean;
}) => {
  // since next/back need to be refreshed on every state change, we
  // are not using useShallow or selectors here.
  const actionConfig = useActionConfigStore();
  const eventConfig = useEventStore();
  const runMetricConfig = useRunMetricThresholdStore();
  const runMetricChangeConfig = useRunMetricChangeStore();
  const runStateConfig = useRunStateStore();
  const runMetricZscoreConfig = useRunMetricZscoreStore();
  let nextButtonDisabled = true;
  // Enable or disable button to go to the next page based on user input

  switch (step) {
    case Steps.BASIC_METRIC_NOTIFICATION:
      return <BasicPageFooter createTrigger={createTrigger} />;
    case Steps.EVENT_CONFIGURATION:
      nextButtonDisabled = !isEventValid(
        eventConfig,
        runMetricConfig,
        runMetricChangeConfig,
        runStateConfig,
        runMetricZscoreConfig
      );
      break;
    case Steps.SUMMARY:
      nextButtonDisabled = automationNameConflict;
      break;
    case Steps.ACTION_CONFIGURATION:
      nextButtonDisabled = !isValidActionConfig(actionConfig);
      break;
    default:
      assertUnreachable(step);
  }
  return (
    <S.Footer>
      {step === Steps.EVENT_CONFIGURATION && (
        <S.HelpMessage>
          {' '}
          You'll configure the action in the next step
        </S.HelpMessage>
      )}
      {step !== Steps.EVENT_CONFIGURATION && (
        <S.FooterLeftContent>
          <Button
            variant="secondary"
            size="large"
            onClick={() => {
              setStep(step - 1);
            }}>
            Back
          </Button>
        </S.FooterLeftContent>
      )}
      {step !== Steps.SUMMARY && (
        <S.FooterRightContent>
          <Button
            variant="primary"
            size="large"
            onClick={() => {
              setStep(step + 1);
            }}
            disabled={nextButtonDisabled}>
            Next step
          </Button>
        </S.FooterRightContent>
      )}
      {step === Steps.SUMMARY && (
        <S.FooterRightContent>
          <Button
            variant="primary"
            size="large"
            onClick={() => createTrigger(triggerName)}
            disabled={
              triggerName === '' ||
              triggerName == null ||
              loadingCreateQueue ||
              loadingCreateAutomation ||
              nextButtonDisabled
            }>
            {eventConfig.initProps.isUpdating
              ? 'Update automation'
              : 'Create automation'}
          </Button>
        </S.FooterRightContent>
      )}
    </S.Footer>
  );
};

export const getEventTriggerConfig = (
  eventConfig: EventConfig,
  runMetricConfig: RunMetricThresholdConfig,
  runMetricChangeConfig: RunMetricChangeConfig,
  runStateConfig: RunStateConfig,
  runMetricZscoreConfig: RunMetricZscoreConfig
):
  | {success: true; triggerEventConfig: TriggerEventConfig}
  | {success: false; toastMessage: string} => {
  let triggerEventConfig: TriggerEventConfig | undefined;

  switch (eventConfig.eventType) {
    case EventTriggeringConditionType.RunMetric:
      if (
        !isValidRunMetricThresholdConfig(runMetricConfig) ||
        eventConfig.projectID === undefined
      ) {
        // This should never happen - config validations in next buttons should have caught this earlier.
        return {
          success: false,
          toastMessage:
            'There was an error with your run metric config. Please refresh the page and try again.',
        };
      }
      triggerEventConfig = createTriggerEventConfigForRunMetric(
        eventConfig.projectID!,
        {
          runName: runMetricConfig.runFilter.runName,
          username: runMetricConfig.runFilter.username!,
          isRunNameFilterEnabled:
            runMetricConfig.runFilter.isRunNameFilterEnabled,
          isUsernameFilterEnabled:
            runMetricConfig.runFilter.isUsernameFilterEnabled,
        },
        {
          metricName: runMetricConfig.metricFilter.metricName!,
          threshold: runMetricConfig.metricFilter.threshold!,
          windowSize: runMetricConfig.metricFilter.windowSize!,
          cmpOp: runMetricConfig.metricFilter.cmpOp!,
          aggOp: runMetricConfig.metricFilter.aggOp!,
          panelLink: runMetricConfig.metricFilter.panelLink,
        }
      );
      break;
    case EventTriggeringConditionType.RunMetricChange:
      if (
        !isValidRunMetricChangeConfig(runMetricChangeConfig) ||
        eventConfig.projectID === undefined
      ) {
        // This should never happen - config validations in next buttons should have caught this earlier.
        return {
          success: false,
          toastMessage:
            'There was an error with your run metric config. Please refresh the page and try again.',
        };
      }
      triggerEventConfig = createTriggerEventConfigForRunMetricChange(
        eventConfig.projectID!,
        {
          runName: runMetricChangeConfig.runFilter.runName,
          username: runMetricChangeConfig.runFilter.username!,
          isRunNameFilterEnabled:
            runMetricChangeConfig.runFilter.isRunNameFilterEnabled,
          isUsernameFilterEnabled:
            runMetricChangeConfig.runFilter.isUsernameFilterEnabled,
        },
        {
          metricName: runMetricChangeConfig.metricFilter.metricName!,
          changeDirection: runMetricChangeConfig.metricFilter.changeDirection!,
          changeAmount: runMetricChangeConfig.metricFilter.changeAmount!,
          priorSize: runMetricChangeConfig.metricFilter.priorSize!,
          currentSize: runMetricChangeConfig.metricFilter.currentSize!,
          aggOp: runMetricChangeConfig.metricFilter.aggOp!,
          changeType: runMetricChangeConfig.metricFilter.changeType!,
          panelLink: runMetricChangeConfig.metricFilter.panelLink,
        }
      );
      break;
    case EventTriggeringConditionType.RunState: {
      if (
        !isValidRunStateConfig(runStateConfig) ||
        eventConfig.projectID === undefined
      ) {
        return {
          success: false,
          toastMessage:
            'There was an error with your run state config. Please refresh the page and try again.',
        };
      }
      triggerEventConfig = createTriggerEventConfigForRunState(
        eventConfig.projectID!,
        {
          runName: runStateConfig.runFilter.runName,
          username: runStateConfig.runFilter.username,
          isRunNameFilterEnabled:
            runStateConfig.runFilter.isRunNameFilterEnabled,
          isUsernameFilterEnabled:
            runStateConfig.runFilter.isUsernameFilterEnabled,
        },
        runStateConfig.runStateFilter
      );
      break;
    }
    case EventTriggeringConditionType.LinkModel:
    case EventTriggeringConditionType.AddArtifactAlias:
    case EventTriggeringConditionType.CreateArtifact:
    case EventTriggeringConditionType.AddArtifactTag:
      triggerEventConfig = {
        triggeringEventType: eventConfig.eventType,
        scopeType: eventConfig.scopeType!,
        scopeID: eventConfig.scopeID!,
        eventFilter: getTriggerEventConfigFilter(eventConfig.filters),
      };
      break;
    case EventTriggeringConditionType.UpdateArtifactAlias:
    case undefined:
      return {
        success: false,
        toastMessage:
          'There was an error. Please refresh the page and try again.',
      };
    case EventTriggeringConditionType.RunMetricZscore: {
      const {
        runFilter: {
          runName,
          username,
          isRunNameFilterEnabled,
          isUsernameFilterEnabled,
        },
        metricFilter,
      } = runMetricZscoreConfig;
      triggerEventConfig = createTriggerEventConfigForRunMetricZscore(
        eventConfig.projectID!,
        {
          runName,
          username,
          isRunNameFilterEnabled,
          isUsernameFilterEnabled,
        },
        metricFilter as ZscoreMetricFilter
      );
      break;
    }
    default:
      assertUnreachable(eventConfig.eventType);
  }
  return {success: true, triggerEventConfig: triggerEventConfig};
};

export const getTriggerEventConfigFilter = (
  filter: TriggerRootFilter
): string => {
  return JSON.stringify(toMongo(filter));
};

export const getActionTriggerConfig = (
  actionConfig: ActionConfig,
  notificationConfig: NotificationConfig,
  webhookConfig: WebhookConfig
):
  | {success: true; triggerActionConfig: TriggerActionConfig}
  | {success: false; toastMessage: string} => {
  let triggerActionConfig;
  switch (actionConfig.actionType) {
    case undefined:
      return {
        success: false,
        toastMessage:
          'There was an error. Please refresh the page and try again.',
      };
    case 'NotificationTriggeredAction':
      if (!isValidNotificationConfig(notificationConfig)) {
        return {
          success: false,
          toastMessage:
            'There was an error with your notification config. Please refresh the page and try again.',
        };
      }
      triggerActionConfig = createTriggerActionConfigForSlack(
        notificationConfig.teamSlackIntegration!.id
      );
      break;
    case 'QueueJobTriggeredAction':
      return {
        success: false,
        toastMessage:
          'We do not currently support run metric events with job launch actions',
      };
    case 'GenericWebhookTriggeredAction':
      if (!isValidWebhookConfig(webhookConfig)) {
        return {
          success: false,
          toastMessage:
            'There was an error with your webhook config. Please refresh the page and try again.',
        };
      }
      triggerActionConfig = createTriggerActionConfigForWebhook(
        webhookConfig.webhook!.id,
        webhookConfig.webhookPayload
      );
      break;
    default:
      assertUnreachable(actionConfig.actionType);
  }
  return {success: true, triggerActionConfig: triggerActionConfig};
};
